import { useCallback, useEffect, useState } from "react";
import { getSlaSettings, saveSlaSettings, DEFAULT_SLA_SETTINGS } from "@/api/slaSettingsApi";

function secondsToMinutesDisplay(seconds) {
  const m = Math.round((seconds / 60) * 10) / 10;
  return `${m} min`;
}

export default function SlaSettingsSection() {
  const [chatFirst, setChatFirst] = useState(String(DEFAULT_SLA_SETTINGS.chatFirstResponseSeconds));
  const [chatAvg, setChatAvg] = useState(String(DEFAULT_SLA_SETTINGS.chatAverageResponseSeconds));
  const [ticketFirst, setTicketFirst] = useState(String(DEFAULT_SLA_SETTINGS.ticketFirstResponseSeconds));
  const [ticketAvg, setTicketAvg] = useState(String(DEFAULT_SLA_SETTINGS.ticketAverageResponseSeconds));
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSlaSettings();
      setChatFirst(String(data.chatFirstResponseSeconds));
      setChatAvg(String(data.chatAverageResponseSeconds));
      setTicketFirst(String(data.ticketFirstResponseSeconds));
      setTicketAvg(String(data.ticketAverageResponseSeconds));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const parsePositive = (raw, fallback) => {
    const n = parseInt(String(raw).replace(/\D/g, ""), 10);
    return Number.isFinite(n) && n >= 1 ? n : fallback;
  };

  const handleSave = async () => {
    setSaved(false);
    await saveSlaSettings({
      chatFirstResponseSeconds: parsePositive(chatFirst, DEFAULT_SLA_SETTINGS.chatFirstResponseSeconds),
      chatAverageResponseSeconds: parsePositive(chatAvg, DEFAULT_SLA_SETTINGS.chatAverageResponseSeconds),
      ticketFirstResponseSeconds: parsePositive(ticketFirst, DEFAULT_SLA_SETTINGS.ticketFirstResponseSeconds),
      ticketAverageResponseSeconds: parsePositive(ticketAvg, DEFAULT_SLA_SETTINGS.ticketAverageResponseSeconds),
    });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <p className="text-sm text-slate-500">Loading SLA settings…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-slate-900">SLAs</h3>
        <p className="mt-1 text-sm text-slate-500">
          Targets for first human reply and ongoing reply time. Chats and tickets that exceed these thresholds are highlighted in red in the inbox so agents can prioritize.
        </p>
      </div>

      <div className="ui-card-static space-y-8 !p-6 sm:!p-8">
        <section aria-labelledby="sla-chat-heading">
          <h4 id="sla-chat-heading" className="text-sm font-semibold text-slate-900">
            Live chat
          </h4>
          <p className="mt-1 text-xs text-slate-500">Measured from customer timestamps in the thread (human agent only).</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="sla-chat-first" className="mb-2 block text-sm font-semibold text-slate-800">
                First response time (seconds)
              </label>
              <input
                id="sla-chat-first"
                type="number"
                min={1}
                value={chatFirst}
                onChange={(e) => setChatFirst(e.target.value)}
                className="ui-field"
              />
              <p className="mt-1 text-[11px] text-slate-400">Max time until the first agent message after the customer opens the thread.</p>
            </div>
            <div>
              <label htmlFor="sla-chat-avg" className="mb-2 block text-sm font-semibold text-slate-800">
                Response time (seconds)
              </label>
              <input
                id="sla-chat-avg"
                type="number"
                min={1}
                value={chatAvg}
                onChange={(e) => setChatAvg(e.target.value)}
                className="ui-field"
              />
              <p className="mt-1 text-[11px] text-slate-400">Max wait after the latest customer message until an agent replies.</p>
            </div>
          </div>
        </section>

        <section className="border-t border-slate-100 pt-8" aria-labelledby="sla-ticket-heading">
          <h4 id="sla-ticket-heading" className="text-sm font-semibold text-slate-900">
            Tickets
          </h4>
          <p className="mt-1 text-xs text-slate-500">
            Use seconds for precision (for example 3600 = 1 hour). Current ticket targets: first{" "}
            <span className="font-medium text-slate-700">{secondsToMinutesDisplay(parsePositive(ticketFirst, 3600))}</span>, ongoing{" "}
            <span className="font-medium text-slate-700">{secondsToMinutesDisplay(parsePositive(ticketAvg, 3600))}</span>.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="sla-ticket-first" className="mb-2 block text-sm font-semibold text-slate-800">
                First response time (seconds)
              </label>
              <input
                id="sla-ticket-first"
                type="number"
                min={1}
                value={ticketFirst}
                onChange={(e) => setTicketFirst(e.target.value)}
                className="ui-field"
              />
            </div>
            <div>
              <label htmlFor="sla-ticket-avg" className="mb-2 block text-sm font-semibold text-slate-800">
                Response time (seconds)
              </label>
              <input
                id="sla-ticket-avg"
                type="number"
                min={1}
                value={ticketAvg}
                onChange={(e) => setTicketAvg(e.target.value)}
                className="ui-field"
              />
              <p className="mt-1 text-[11px] text-slate-400">Time since the last customer activity on the ticket while awaiting an agent.</p>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-6">
          <button type="button" className="ui-btn-primary" onClick={() => void handleSave()}>
            Save SLA settings
          </button>
          {saved && <span className="text-sm font-medium text-emerald-700">Saved — inbox will use these thresholds.</span>}
        </div>
      </div>
    </div>
  );
}
